import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";

import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";
import Chip from "@app/component/Chip";
import ListWithOverflowItem from "@app/component/ListWithOverflowItem";
import { getActivityTypes } from "@app/store/activity";
import { RootState } from "@app/store/rootReducer";

import styles from "./NextActivities.module.scss";

interface Props {
  activityTypeIds?: ActivityType["id"][];
  activityTypes: ActivityType[];
}

const EntityTypes = ({ activityTypeIds, activityTypes }: Props) => {
  const intl = useIntl();

  const getTypeChip = useCallback(
    (type: ActivityType) => (
      <Chip
        className={styles.activityType}
        icon={<ActivityTypeIcon activityType={type} className={styles.activityTypeIcon} />}
        key={type.id}
        type="light"
      >
        <span>{type.name}</span>
      </Chip>
    ),
    []
  );

  const showedActivityTypes: ActivityType[] = useMemo(
    () =>
      activityTypeIds
        ? [...activityTypes.filter(({ id }) => activityTypeIds.includes(id))].sort((a, b) =>
            a.name.localeCompare(b.name)
          )
        : [],
    [activityTypeIds, activityTypes]
  );

  const items = useMemo(
    () => [
      <span key="text">
        {intl.formatMessage({
          id: "frequency.frequencyModal.activities.next.suggestion.typeList",
          defaultMessage: "Schedule a",
          description:
            "frequency component - frequency modal - next activities - suggestion type list",
        })}
      </span>,
      ...showedActivityTypes.map(getTypeChip),
    ],
    [intl, getTypeChip, showedActivityTypes]
  );

  const activityTypeOverflowRender = useCallback(
    (count: number) => (
      <Tooltip
        title={
          <div className={styles.overflowTooltip}>
            {showedActivityTypes.slice(showedActivityTypes.length - count).map(getTypeChip)}
          </div>
        }
      >
        <div className={styles.activityTypeOverflow}>
          {intl.formatMessage(
            {
              id: "frequency.frequencyModal.activities.next.activityTypeOverflow.text",
              defaultMessage: "or + {count} more",
              description:
                "frequency component - frequency modal - next activities - activity type overflow - count",
            },
            { count }
          )}
        </div>
      </Tooltip>
    ),
    [getTypeChip, intl, showedActivityTypes]
  );

  return (
    <div>
      {activityTypeIds?.length ? (
        <ListWithOverflowItem
          className={styles.activityTypeList}
          overflowRender={activityTypeOverflowRender}
        >
          {items}
        </ListWithOverflowItem>
      ) : (
        <div>
          {intl.formatMessage({
            id: "frequency.frequencyModal.activities.next.suggestion.anyActivity",
            defaultMessage: "Schedule any activity",
            description: "frequency component - frequency modal - next activities - any activity",
          })}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
});

export default connect(mapStateToProps)(EntityTypes);
